// ─── Inkoop / Besteltraject (batch-4 inkoop) — types + Zod-schemas ───────────
//
// Aanvragen (3 routes), offerteaanvragen (RFQ), leverancier-offertes,
// goederenontvangst, grensbedragen, budgetten. Datamodel: zie
// packages/db/prisma/schema.prisma (onderaan). Pure rekenlogica:
// packages/utils/src/inkoop-besteltraject.ts.
//
// De Zod-schemas zijn de ENE bron: de API parset ermee en de frontend
// valideert ermee (CLAUDE.md "Validatie & foutmeldingen").

import { z } from 'zod'
import { idParam } from './id-validators'

const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet jjjj-mm-dd zijn')

type BadgeVariant = 'info' | 'warning' | 'success' | 'danger' | 'secondary'

// ─── Enums (mirror Prisma) + labels ──────────────────────────────────────────

export const INKOOP_AANVRAAG_STATUS = [
  'CONCEPT',
  'INGEDIEND',
  'WACHT_OP_BEOORDELING',
  'GOEDGEKEURD',
  'AFGEWEZEN',
  'OMGEZET_NAAR_OFFERTEAANVRAAG',
  'OMGEZET_NAAR_BESTELLING',
  'GEANNULEERD',
] as const
export type InkoopAanvraagStatus = (typeof INKOOP_AANVRAAG_STATUS)[number]

export const INKOOP_AANVRAAG_STATUS_LABELS: Record<InkoopAanvraagStatus, string> = {
  CONCEPT: 'Concept',
  INGEDIEND: 'Ingediend',
  WACHT_OP_BEOORDELING: 'Wacht op beoordeling',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEWEZEN: 'Afgewezen',
  OMGEZET_NAAR_OFFERTEAANVRAAG: 'Offerte aangevraagd',
  OMGEZET_NAAR_BESTELLING: 'Omgezet naar bestelling',
  GEANNULEERD: 'Geannuleerd',
}

export const INKOOP_AANVRAAG_STATUS_BADGE_VARIANT: Record<InkoopAanvraagStatus, BadgeVariant> = {
  CONCEPT: 'secondary',
  INGEDIEND: 'info',
  WACHT_OP_BEOORDELING: 'warning',
  GOEDGEKEURD: 'success',
  AFGEWEZEN: 'danger',
  OMGEZET_NAAR_OFFERTEAANVRAAG: 'info',
  OMGEZET_NAAR_BESTELLING: 'success',
  GEANNULEERD: 'secondary',
}

export const INKOOP_AANVRAAG_BRON = ['PORTAL', 'MINIMUMVOORRAAD', 'BACKOFFICE'] as const
export type InkoopAanvraagBron = (typeof INKOOP_AANVRAAG_BRON)[number]

export const INKOOP_AANVRAAG_BRON_LABELS: Record<InkoopAanvraagBron, string> = {
  PORTAL: 'Via de app',
  MINIMUMVOORRAAD: 'Minimumvoorraad',
  BACKOFFICE: 'Backoffice',
}

export const OFFERTEAANVRAAG_STATUS = [
  'CONCEPT',
  'VERZONDEN',
  'WACHT_OP_REACTIE',
  'OFFERTE_ONTVANGEN',
  'IN_BEOORDELING',
  'GOEDGEKEURD',
  'AFGEWEZEN',
  'VERLOPEN',
] as const
export type OfferteaanvraagStatus = (typeof OFFERTEAANVRAAG_STATUS)[number]

export const OFFERTEAANVRAAG_STATUS_LABELS: Record<OfferteaanvraagStatus, string> = {
  CONCEPT: 'Concept',
  VERZONDEN: 'Verzonden',
  WACHT_OP_REACTIE: 'Wacht op reactie',
  OFFERTE_ONTVANGEN: 'Offerte ontvangen',
  IN_BEOORDELING: 'In beoordeling',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEWEZEN: 'Afgewezen',
  VERLOPEN: 'Verlopen',
}

export const OFFERTEAANVRAAG_STATUS_BADGE_VARIANT: Record<OfferteaanvraagStatus, BadgeVariant> = {
  CONCEPT: 'secondary',
  VERZONDEN: 'info',
  WACHT_OP_REACTIE: 'warning',
  OFFERTE_ONTVANGEN: 'info',
  IN_BEOORDELING: 'warning',
  GOEDGEKEURD: 'success',
  AFGEWEZEN: 'danger',
  VERLOPEN: 'secondary',
}

export const ONTVANGST_STATUS = [
  'NOG_NIET_ONTVANGEN',
  'ONTVANGEN',
  'CONTROLE_NODIG',
  'AFGEKEURD_RETOUR',
  'BINNENGEBOEKT',
  'OP_VOORRAAD',
  'UITGEGEVEN_AAN_MEDEWERKER',
] as const
export type OntvangstStatus = (typeof ONTVANGST_STATUS)[number]

export const ONTVANGST_STATUS_LABELS: Record<OntvangstStatus, string> = {
  NOG_NIET_ONTVANGEN: 'Nog niet ontvangen',
  ONTVANGEN: 'Ontvangen',
  CONTROLE_NODIG: 'Controle nodig',
  AFGEKEURD_RETOUR: 'Afgekeurd / retour',
  BINNENGEBOEKT: 'Binnengeboekt',
  OP_VOORRAAD: 'Op voorraad',
  UITGEGEVEN_AAN_MEDEWERKER: 'Uitgegeven aan medewerker',
}

export const ONTVANGST_STATUS_BADGE_VARIANT: Record<OntvangstStatus, BadgeVariant> = {
  NOG_NIET_ONTVANGEN: 'secondary',
  ONTVANGEN: 'info',
  CONTROLE_NODIG: 'warning',
  AFGEKEURD_RETOUR: 'danger',
  BINNENGEBOEKT: 'success',
  OP_VOORRAAD: 'success',
  UITGEGEVEN_AAN_MEDEWERKER: 'success',
}

export const INKOOP_GOEDKEURING_STATUS = [
  'GEEN',
  'WACHT_OP_TEAMLEIDER',
  'WACHT_OP_KASPER',
  'WACHT_OP_DIRECTIE',
  'WACHT_OP_FINANCE',
  'GOEDGEKEURD',
  'AFGEWEZEN',
] as const
export type InkoopGoedkeuringStatus = (typeof INKOOP_GOEDKEURING_STATUS)[number]

export const INKOOP_GOEDKEURING_STATUS_LABELS: Record<InkoopGoedkeuringStatus, string> = {
  GEEN: 'Geen goedkeuring nodig',
  WACHT_OP_TEAMLEIDER: 'Wacht op teamleider',
  WACHT_OP_KASPER: 'Wacht op Kasper',
  WACHT_OP_DIRECTIE: 'Wacht op directie',
  WACHT_OP_FINANCE: 'Wacht op finance',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEWEZEN: 'Afgewezen',
}

export const INKOOP_GOEDKEURING_STATUS_BADGE_VARIANT: Record<InkoopGoedkeuringStatus, BadgeVariant> = {
  GEEN: 'secondary',
  WACHT_OP_TEAMLEIDER: 'warning',
  WACHT_OP_KASPER: 'warning',
  WACHT_OP_DIRECTIE: 'warning',
  WACHT_OP_FINANCE: 'warning',
  GOEDGEKEURD: 'success',
  AFGEWEZEN: 'danger',
}

// Afwijs-redenen bij een aanvraag/offerte (Deel 11).
export const INKOOP_AFWIJS_REDEN = [
  'TE_DUUR',
  'NIET_NODIG',
  'ALTERNATIEF',
  'NIET_GOEDGEKEURD_KLANT',
  'BUDGET_OVERSCHREDEN',
  'ANDERS',
] as const
export type InkoopAfwijsReden = (typeof INKOOP_AFWIJS_REDEN)[number]

export const INKOOP_AFWIJS_REDEN_LABELS: Record<InkoopAfwijsReden, string> = {
  TE_DUUR: 'Te duur',
  NIET_NODIG: 'Niet nodig',
  ALTERNATIEF: 'Alternatief beschikbaar',
  NIET_GOEDGEKEURD_KLANT: 'Niet goedgekeurd door klant',
  BUDGET_OVERSCHREDEN: 'Budget overschreden',
  ANDERS: 'Anders',
}

// ─── DTOs ─────────────────────────────────────────────────────────────────────

export interface InkoopAanvraagRegelDTO {
  id: string
  voorraadArtikelId: string | null
  omschrijving: string
  aantal: number
  eenheid: string | null
  maat: string | null
  uniekItem: boolean
  geschattePrijs: number | null
}

export interface InkoopAanvraagAuditDTO {
  id: string
  actie: string
  actorNaam: string
  oudeStatus: string | null
  nieuweStatus: string | null
  bedrag: number | null
  reden: string | null
  createdAt: string
}

export interface InkoopAanvraagListRowDTO {
  id: string
  aanvraagNummer: string
  status: InkoopAanvraagStatus
  bron: InkoopAanvraagBron
  aanvragerNaam: string
  voorMedewerkerNaam: string | null
  bvNaam: string | null
  reden: string | null
  geschatBedrag: number | null
  goedkeuringStatus: InkoopGoedkeuringStatus
  aantalRegels: number
  gewensteDatum: string | null
  createdAt: string
}

export interface InkoopAanvraagDetailDTO extends InkoopAanvraagListRowDTO {
  voorMedewerkerId: string | null
  bvId: string | null
  clientId: string | null
  clientNaam: string | null
  werkbonId: string | null
  opmerking: string | null
  afwijzingReden: string | null
  beoordeeldDoorNaam: string | null
  beoordeeldOp: string | null
  regels: InkoopAanvraagRegelDTO[]
  offerteaanvraagIds: string[]
  orderIds: string[]
  audits: InkoopAanvraagAuditDTO[]
}

/** Portal "Mijn aanvragen" — alleen wat de medewerker mag zien. */
export interface InkoopMijnAanvraagDTO {
  id: string
  aanvraagNummer: string
  status: InkoopAanvraagStatus
  artikelen: string // korte samenvatting van de regels
  aantalRegels: number
  verwachteLeverdatum: string | null
  afwijzingReden: string | null
  opmerking: string | null
  createdAt: string
}

export interface OfferteaanvraagRegelDTO {
  id: string
  omschrijving: string
  aantal: number
  specificatie: string | null
  voorraadArtikelId: string | null
  artikelnummer: string | null
  merk: string | null
  eenheid: string | null
  fotos: OfferteaanvraagBijlageDTO[]
}

export interface OfferteaanvraagBijlageDTO {
  id: string
  fileName: string
  mimeType: string
}

export const OFFERTEAANVRAAG_ONTVANGER_STATUS = ['CONCEPT', 'VERZONDEN', 'MISLUKT'] as const
export type OfferteaanvraagOntvangerStatus = (typeof OFFERTEAANVRAAG_ONTVANGER_STATUS)[number]

export const OFFERTEAANVRAAG_ONTVANGER_STATUS_LABELS: Record<OfferteaanvraagOntvangerStatus, string> = {
  CONCEPT: 'Nog niet verstuurd',
  VERZONDEN: 'Verstuurd',
  MISLUKT: 'Versturen mislukt',
}

/** Een benaderde leverancier op een offerteaanvraag (eigen mail + aanhef). */
export interface OfferteaanvraagOntvangerDTO {
  id: string
  leverancierId: string
  leverancierNaam: string
  contactId: string | null
  contactNaam: string | null
  email: string | null
  /** De aanhef zoals hij in de mail komt (opgeslagen of afgeleid). */
  aanhef: string
  status: OfferteaanvraagOntvangerStatus
  verzondenOp: string | null
  mailStatus: string | null
  /** Of deze leverancier al een offerte op de aanvraag heeft. */
  heeftOfferte: boolean
}

/** Contactpersoon-keuze voor een ontvanger (alleen contacten met e-mail). */
export interface RfqContactOptieDTO {
  id: string
  naam: string
  email: string
  functie: string | null
  aanhef: string
}

/** Suggestie per leverancier: adres + contacten + de standaard-aanhef. */
export interface RfqOntvangerSuggestieDTO {
  leverancierId: string
  leverancierNaam: string
  /** Algemeen adres van de relatie (email, anders factuur-email). */
  algemeenEmail: string | null
  contacten: RfqContactOptieDTO[]
  /** Voorstel: eerste contact met e-mail, anders null (= algemeen adres). */
  voorgesteldContactId: string | null
  voorgesteldEmail: string | null
  voorgesteldeAanhef: string
  /** Of de leverancier offertes mag ontvangen (niet geblokkeerd). */
  benaderbaar: boolean
  blokkadeReden: string | null
}

/** Artikel-keuze in het RFQ-formulier: met de velden die de regel voorvult. */
export interface RfqArtikelOptieDTO {
  value: string
  label: string
  hint: string
  naam: string
  artikelnummer: string | null
  merk: string | null
  eenheid: string | null
  omschrijving: string | null
}

export interface LeverancierOfferteRegelDTO {
  id: string
  omschrijving: string
  aantal: number
  prijsPerStuk: number
  btwPercentage: number
  voorraadArtikelId: string | null
}

/**
 * FO-079 — afgeleide weergave-status van een offerte (geen DB-kolom). Mirror van
 * `OfferteWeergaveStatus` in `@streep/utils` (`offerteWeergaveStatus`), die 'm uit
 * `goedgekeurd`/`geldigTot` + inhoud afleidt. `@streep/types` mag niet van
 * `@streep/utils` afhangen, dus het type staat hier gespiegeld.
 */
export type OfferteWeergaveStatus = 'CONCEPT' | 'ONTVANGEN' | 'GEKOZEN' | 'AFGEWEZEN' | 'VERLOPEN'

export const OFFERTE_WEERGAVE_STATUS_LABELS: Record<OfferteWeergaveStatus, string> = {
  CONCEPT: 'Concept',
  ONTVANGEN: 'Ontvangen',
  GEKOZEN: 'Gekozen',
  AFGEWEZEN: 'Afgewezen',
  VERLOPEN: 'Verlopen',
}

export const OFFERTE_WEERGAVE_STATUS_BADGE_VARIANT: Record<OfferteWeergaveStatus, BadgeVariant> = {
  CONCEPT: 'secondary',
  ONTVANGEN: 'info',
  GEKOZEN: 'success',
  AFGEWEZEN: 'danger',
  VERLOPEN: 'secondary',
}

export interface LeverancierOfferteDTO {
  id: string
  offerteaanvraagId: string
  leverancierId: string | null
  leverancierNaam: string | null
  offertebedrag: number | null
  levertijd: string | null
  geldigTot: string | null
  betalingscondities: string | null
  beoordeeld: boolean
  goedgekeurd: boolean | null
  beoordeeldDoorNaam: string | null
  beoordeeldOp: string | null
  afwijzingReden: string | null
  documentId: string | null
  regels: LeverancierOfferteRegelDTO[]
  createdAt: string
  // FO-079 — afgeleide weergave-status + totalen uit de regels (server berekend,
  // Decimal -> number; btw per regel-fractie, afgerond op het laatste moment).
  weergaveStatus: OfferteWeergaveStatus
  totaalExclBtw: number
  totaalBtw: number
  totaalInclBtw: number
  /** Of dit de gekozen offerte van de aanvraag is (goedgekeurd === true). */
  gekozen: boolean
  /** FO-002 — of de leverancier de voorkeursleverancier is (markering in vergelijk). */
  leverancierVoorkeur: boolean
}

export interface OfferteaanvraagListRowDTO {
  id: string
  rfqNummer: string
  status: OfferteaanvraagStatus
  leverancierNaam: string | null
  bvNaam: string | null
  aanvraagNummer: string | null
  deadlineOfferte: string | null
  verzondenOp: string | null
  aantalOffertes: number
  createdAt: string
  /** Aantal benaderde leveranciers (0 = oude flow met een enkele leverancier). */
  aantalOntvangers: number
  categorieNaam: string | null
}

export interface OfferteaanvraagDetailDTO extends OfferteaanvraagListRowDTO {
  aanvraagId: string | null
  leverancierId: string | null
  leverancierVrij: string | null
  bvId: string | null
  gewensteLevertijd: string | null
  afleveradres: string | null
  contactpersoon: string | null
  opmerking: string | null
  mailIngestId: string | null
  categorieId: string | null
  regels: OfferteaanvraagRegelDTO[]
  offertes: LeverancierOfferteDTO[]
  ontvangers: OfferteaanvraagOntvangerDTO[]
}

export interface OntvangstRegelDTO {
  id: string
  inkoopOrderRegelId: string | null
  artikel: string | null
  besteldAantal: number | null
  aantalOntvangen: number
  voorraadArtikelId: string | null
  uniekItemId: string | null
  binnengeboekt: boolean
}

export interface GoederenontvangstListRowDTO {
  id: string
  inkoopOrderId: string
  poNummer: string
  leverancierNaam: string | null
  status: OntvangstStatus
  ontvangenOp: string | null
  pakbonNummer: string | null
  verwachtDatum: string | null
  aantalRegels: number
  createdAt: string
}

export interface GoederenontvangstDetailDTO extends GoederenontvangstListRowDTO {
  ontvangenDoorNaam: string | null
  factuurNummer: string | null
  controleAkkoord: boolean
  opmerking: string | null
  regels: OntvangstRegelDTO[]
}

export interface InkoopGrensbedragDTO {
  id: string
  scope: string
  rol: string | null
  medewerkerId: string | null
  medewerkerNaam: string | null
  bvId: string | null
  bvNaam: string | null
  artikelgroep: string | null
  magAanvragenTot: number | null
  magGoedkeurenTot: number | null
  extraGoedkeuringVanaf: number | null
  goedkeuringBijNieuwArtikel: boolean
  goedkeuringBijNieuweLeverancier: boolean
  createdAt: string
}

export interface InkoopBudgetDTO {
  id: string
  scope: string
  bvId: string | null
  bvNaam: string | null
  medewerkerId: string | null
  medewerkerNaam: string | null
  projectId: string | null
  clientId: string | null
  clientNaam: string | null
  artikelgroep: string | null
  periodeVan: string
  periodeTot: string
  bedrag: number
  // FO-392 (batch-46) — twee systeem-tellers + de read-only som `besteed`.
  // `besteed` = besteedObligo + besteedGerealiseerd (afgeleid; niet handmatig
  // muteerbaar). `resterend` = bedrag - besteed.
  besteed: number
  besteedObligo: number
  besteedGerealiseerd: number
  budgethouderId: string | null
  budgethouderNaam: string | null
  resterend: number
  createdAt: string
}

/** Besteladvies-tegel voor een voorraadartikel onder minimum (Route 2). */
export interface InkoopBesteladviesDTO {
  artikelId: string
  artikelNummer: string
  naam: string
  huidigeVoorraad: number
  minimumVoorraad: number
  reedsBesteld: number
  adviesAantal: number
  tekort: number
  leverancier: string | null
  inkoopPrijs: number | null
}

/** Dashboard-tegels (Deel 13). */
export interface InkoopBesteltrajectStatsDTO {
  nieuweAanvragen: number
  wachtOpOfferte: number
  offertesTeBeoordelen: number
  wachtOpGoedkeuring: number
  besteldOnderweg: number
  leverdatumVerlopen: number
  binnenkomendeGoederen: number
  onderMinimum: number
  budgetOverschrijdingen: number
}

// ─── Zod request-schemas ──────────────────────────────────────────────────────

export const inkoopAanvraagRegelInputSchema = z.object({
  voorraadArtikelId: idParam().optional().nullable(),
  omschrijving: z.string().trim().min(1).max(500),
  aantal: z.number().int().positive().default(1),
  eenheid: z.string().trim().max(50).optional().nullable(),
  maat: z.string().trim().max(50).optional().nullable(),
  uniekItem: z.boolean().optional().default(false),
  geschattePrijs: z.number().nonnegative().optional().nullable(),
})
export type InkoopAanvraagRegelInput = z.infer<typeof inkoopAanvraagRegelInputSchema>

export const inkoopAanvraagCreateSchema = z.object({
  // bron default BACKOFFICE; de portal-route forceert PORTAL serverside.
  bron: z.enum(INKOOP_AANVRAAG_BRON).optional().default('BACKOFFICE'),
  voorMedewerkerId: idParam().optional().nullable(),
  // FO-320 — besteltraject-drager (kar/koffer) + veroorzakende controle: geeft de
  // latere factuurregel automatisch deze kar als toewijzing-suggestie (BESTELTRAJECT).
  gereedschapsDragerId: idParam().optional().nullable(),
  dragerControleId: idParam().optional().nullable(),
  bvId: idParam().optional().nullable(),
  clientId: idParam().optional().nullable(),
  werkbonId: idParam().optional().nullable(),
  // Batch-12 FO-002 — optionele voorkeursleverancier (portal: monteur kiest een
  // goedgekeurde leverancier). De API valideert dat hij bestelbaar is.
  voorkeurLeverancierId: idParam().optional().nullable(),
  reden: z.string().trim().max(2000).optional().nullable(),
  gewensteDatum: dateString.optional().nullable(),
  opmerking: z.string().trim().max(2000).optional().nullable(),
  regels: z.array(inkoopAanvraagRegelInputSchema).min(1, 'Minstens één regel vereist'),
})
export type InkoopAanvraagCreateInput = z.infer<typeof inkoopAanvraagCreateSchema>

export const inkoopAanvraagUpdateSchema = z.object({
  voorMedewerkerId: idParam().optional().nullable(),
  gereedschapsDragerId: idParam().optional().nullable(),
  dragerControleId: idParam().optional().nullable(),
  bvId: idParam().optional().nullable(),
  clientId: idParam().optional().nullable(),
  werkbonId: idParam().optional().nullable(),
  reden: z.string().trim().max(2000).optional().nullable(),
  gewensteDatum: dateString.optional().nullable(),
  opmerking: z.string().trim().max(2000).optional().nullable(),
  regels: z.array(inkoopAanvraagRegelInputSchema).min(1).optional(),
})

export const inkoopAanvraagListQuerySchema = z.object({
  status: z.enum(INKOOP_AANVRAAG_STATUS).optional(),
  bron: z.enum(INKOOP_AANVRAAG_BRON).optional(),
  goedkeuringStatus: z.enum(INKOOP_GOEDKEURING_STATUS).optional(),
  aanvragerId: idParam().optional(),
  voorMedewerkerId: idParam().optional(),
  bvId: idParam().optional(),
  search: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
})

export const inkoopAanvraagAfwijzenSchema = z.object({
  redenCode: z.enum(INKOOP_AFWIJS_REDEN).optional(),
  reden: z.string().trim().min(1, 'Reden is verplicht').max(2000),
})

export const inkoopAanvraagMeerInfoSchema = z.object({
  vraag: z.string().trim().min(1).max(2000),
})

// Vanuit een aanvraag een offerteaanvraag spawnen (Deel 4 -> Deel 5).
export const inkoopAanvraagNaarOfferteSchema = z.object({
  leverancierId: idParam().optional().nullable(),
  leverancierVrij: z.string().trim().max(200).optional().nullable(),
  gewensteLevertijd: z.string().trim().max(200).optional().nullable(),
  deadlineOfferte: dateString.optional().nullable(),
  afleveradres: z.string().trim().max(500).optional().nullable(),
  contactpersoon: z.string().trim().max(200).optional().nullable(),
  opmerking: z.string().trim().max(2000).optional().nullable(),
})

// Vanuit een aanvraag direct een bestelling (Deel 4 -> Deel 7).
export const inkoopAanvraagNaarBestellingSchema = z.object({
  leverancierId: idParam(),
  btwPercentage: z.number().min(0).max(100).default(21),
  verwachtDatum: dateString.optional().nullable(),
  regels: z
    .array(
      z.object({
        artikel: z.string().trim().min(1).max(500),
        aantal: z.number().positive(),
        prijs: z.number().nonnegative(),
        btwPercentage: z.number().min(0).max(100).optional().nullable(),
        voorraadArtikelId: idParam().optional().nullable(),
      }),
    )
    .min(1),
})

// ─── Offerteaanvraag (RFQ) ────────────────────────────────────────────────────

export const offerteaanvraagRegelInputSchema = z.object({
  omschrijving: z.string().trim().min(1, 'Omschrijving is verplicht').max(500),
  aantal: z.number().int().positive().default(1),
  specificatie: z.string().trim().max(1000).optional().nullable(),
  // Bestaand artikel (artikelnummer bij een nieuwe leverancier) of nieuw artikel.
  voorraadArtikelId: idParam().optional().nullable(),
  artikelnummer: z.string().trim().max(100).optional().nullable(),
  merk: z.string().trim().max(120).optional().nullable(),
  eenheid: z.string().trim().max(40).optional().nullable(),
})

/** Een te benaderen leverancier; adres/aanhef leeg = afleiden uit de relatie. */
export const offerteaanvraagOntvangerInputSchema = z.object({
  leverancierId: idParam(),
  contactId: idParam().optional().nullable(),
  email: z.string().trim().email('Ongeldig e-mailadres').max(200).optional().nullable(),
  aanhef: z.string().trim().max(200).optional().nullable(),
})
export type OfferteaanvraagOntvangerInput = z.infer<typeof offerteaanvraagOntvangerInputSchema>

/** Max. leveranciers in een keer (elke krijgt een eigen mail). */
export const MAX_RFQ_ONTVANGERS = 15

export const offerteaanvraagCreateSchema = z.object({
  aanvraagId: idParam().optional().nullable(),
  leverancierId: idParam().optional().nullable(),
  leverancierVrij: z.string().trim().max(200).optional().nullable(),
  bvId: idParam().optional().nullable(),
  gewensteLevertijd: z.string().trim().max(200).optional().nullable(),
  deadlineOfferte: dateString.optional().nullable(),
  afleveradres: z.string().trim().max(500).optional().nullable(),
  contactpersoon: z.string().trim().max(200).optional().nullable(),
  opmerking: z.string().trim().max(2000).optional().nullable(),
  categorieId: idParam().optional().nullable(),
  regels: z.array(offerteaanvraagRegelInputSchema).min(1, 'Voeg minimaal een regel toe'),
  ontvangers: z
    .array(offerteaanvraagOntvangerInputSchema)
    .max(MAX_RFQ_ONTVANGERS, `Maximaal ${MAX_RFQ_ONTVANGERS} leveranciers per aanvraag`)
    .optional()
    .refine((l) => !l || new Set(l.map((o) => o.leverancierId)).size === l.length, {
      message: 'Een leverancier staat dubbel in de lijst',
    }),
})
export type OfferteaanvraagCreateInput = z.infer<typeof offerteaanvraagCreateSchema>

export const offerteaanvraagUpdateSchema = z.object({
  leverancierId: idParam().optional().nullable(),
  leverancierVrij: z.string().trim().max(200).optional().nullable(),
  bvId: idParam().optional().nullable(),
  gewensteLevertijd: z.string().trim().max(200).optional().nullable(),
  deadlineOfferte: dateString.optional().nullable(),
  afleveradres: z.string().trim().max(500).optional().nullable(),
  contactpersoon: z.string().trim().max(200).optional().nullable(),
  opmerking: z.string().trim().max(2000).optional().nullable(),
  regels: z.array(offerteaanvraagRegelInputSchema).min(1).optional(),
})

export const offerteaanvraagVerzendSchema = z.object({
  // Optioneel het e-mailadres overschrijven; anders het leverancier-adres.
  // Alleen voor de oude flow zonder ontvangers.
  naarEmail: z.string().email().optional(),
  extraBericht: z.string().trim().max(4000).optional().nullable(),
  // Met ontvangers: alleen deze versturen (default: alle nog niet verstuurde).
  ontvangerIds: z.array(idParam()).max(MAX_RFQ_ONTVANGERS).optional(),
  // Ook al verstuurde ontvangers opnieuw mailen (herinnering/correctie).
  opnieuw: z.boolean().optional(),
})

/** Leverancier(s) toevoegen aan een bestaande offerteaanvraag. */
export const offerteaanvraagOntvangersToevoegenSchema = z.object({
  ontvangers: z.array(offerteaanvraagOntvangerInputSchema).min(1).max(MAX_RFQ_ONTVANGERS),
})

/** Contact/adres/aanhef van een nog niet verstuurde ontvanger aanpassen. */
export const offerteaanvraagOntvangerUpdateSchema = z.object({
  contactId: idParam().optional().nullable(),
  email: z.string().trim().email('Ongeldig e-mailadres').max(200).optional().nullable(),
  aanhef: z.string().trim().max(200).optional().nullable(),
})

export const offerteaanvraagListQuerySchema = z.object({
  status: z.enum(OFFERTEAANVRAAG_STATUS).optional(),
  leverancierId: idParam().optional(),
  aanvraagId: idParam().optional(),
  bvId: idParam().optional(),
  search: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
})

// Handmatig een ontvangen offerte vastleggen/koppelen (naast de mailbox-flow).
export const leverancierOfferteRegelInputSchema = z.object({
  omschrijving: z.string().trim().min(1).max(500),
  aantal: z.number().int().positive().default(1),
  prijsPerStuk: z.number().nonnegative(),
  btwPercentage: z.number().min(0).max(100).default(21),
  voorraadArtikelId: idParam().optional().nullable(),
})

export const leverancierOfferteCreateSchema = z.object({
  leverancierId: idParam().optional().nullable(),
  offertebedrag: z.number().nonnegative().optional().nullable(),
  levertijd: z.string().trim().max(200).optional().nullable(),
  geldigTot: dateString.optional().nullable(),
  betalingscondities: z.string().trim().max(500).optional().nullable(),
  documentId: idParam().optional().nullable(),
  regels: z.array(leverancierOfferteRegelInputSchema).optional().default([]),
})

export const leverancierOfferteBeoordeelSchema = z.object({
  besluit: z.enum(['GOEDKEUREN', 'AFWIJZEN']),
  redenCode: z.enum(INKOOP_AFWIJS_REDEN).optional(),
  reden: z.string().trim().max(2000).optional().nullable(),
  // Bij GOEDKEUREN: maak direct een bestelling.
  maakBestelling: z.boolean().optional().default(true),
  btwPercentage: z.number().min(0).max(100).optional().default(21),
})

/**
 * FO-079 — "kies deze offerte": zet de gekozen offerte op gekozen en wijst de
 * overige in dezelfde aanvraag af (met optionele gedeelde reden), in één
 * transactie. Geen verplichte goedkeuringsstap/cascade. Gedeeld Zod-schema (API
 * `safeParse` + form-validatie).
 */
export const offerteKiesSchema = z.object({
  offerteId: idParam(),
  redenAfwijzing: z.string().trim().max(2000).optional().nullable(),
})
export type OfferteKiesInput = z.infer<typeof offerteKiesSchema>

// ─── Goederenontvangst + binnenboeken ─────────────────────────────────────────

export const ontvangstRegelInputSchema = z.object({
  inkoopOrderRegelId: idParam().optional().nullable(),
  aantalOntvangen: z.number().int().nonnegative(),
})

export const goederenontvangstCreateSchema = z.object({
  ontvangenOp: dateString.optional().nullable(),
  pakbonNummer: z.string().trim().max(100).optional().nullable(),
  factuurNummer: z.string().trim().max(100).optional().nullable(),
  controleAkkoord: z.boolean().optional().default(false),
  opmerking: z.string().trim().max(2000).optional().nullable(),
  regels: z.array(ontvangstRegelInputSchema).min(1),
})
export type GoederenontvangstCreateInput = z.infer<typeof goederenontvangstCreateSchema>

// Binnenboeken: bulk via boekMutatie + per-regel uniek item (telefoon/laptop/..)
// + optioneel direct uitgeven aan een medewerker.
export const binnenboekenRegelSchema = z.object({
  ontvangstRegelId: idParam(),
  voorraadArtikelId: idParam().optional().nullable(),
  aantal: z.number().int().positive(),
  // Uniek item (per stuk): wordt een VoorraadUniekItem + QR.
  uniek: z.boolean().optional().default(false),
  serienummer: z.string().trim().max(200).optional().nullable(),
  imei: z.string().trim().max(50).optional().nullable(),
  iccid: z.string().trim().max(50).optional().nullable(),
  merk: z.string().trim().max(100).optional().nullable(),
  model: z.string().trim().max(100).optional().nullable(),
  // Direct uitgeven aan medewerker (anders op voorraad leggen).
  directUitgevenAanId: idParam().optional().nullable(),
})

export const binnenboekenSchema = z.object({
  voorraadLocatieId: idParam().optional().nullable(),
  regels: z.array(binnenboekenRegelSchema).min(1),
})
export type BinnenboekenInput = z.infer<typeof binnenboekenSchema>

export const goederenontvangstListQuerySchema = z.object({
  status: z.enum(ONTVANGST_STATUS).optional(),
  inkoopOrderId: idParam().optional(),
  // Alleen orders die nog (deels) onderweg zijn.
  onderweg: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .optional()
    .transform((v) => (typeof v === 'string' ? v === 'true' : v)),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
})

// ─── Nieuw artikel vanuit inkoop (Route 3) ────────────────────────────────────

export const inkoopNieuwArtikelSchema = z.object({
  naam: z.string().trim().min(1).max(200),
  categorie: z.string().trim().min(1).max(50),
  omschrijving: z.string().trim().max(2000).optional().nullable(),
  merk: z.string().trim().max(100).optional().nullable(),
  type: z.string().trim().max(100).optional().nullable(),
  maat: z.string().trim().max(50).optional().nullable(),
  eenheid: z.string().trim().max(20).optional().nullable(),
  traceerbaar: z.boolean().optional().default(false),
  serienummerVerplicht: z.boolean().optional().default(false),
  imeiVerplicht: z.boolean().optional().default(false),
  qrNodig: z.boolean().optional().default(false),
  minimumVoorraad: z.number().int().nonnegative().optional().default(0),
  leverancier: z.string().trim().max(200).optional().nullable(),
  inkoopPrijs: z.number().nonnegative().optional().nullable(),
  verkoopPrijs: z.number().nonnegative().optional().nullable(),
})
export type InkoopNieuwArtikelInput = z.infer<typeof inkoopNieuwArtikelSchema>

// ─── Grensbedragen + budgetten ────────────────────────────────────────────────

export const INKOOP_GRENSBEDRAG_SCOPE = ['ROL', 'MEDEWERKER', 'BV', 'ARTIKELGROEP'] as const
export const INKOOP_BUDGET_SCOPE = [
  'BV',
  'TEAM',
  'MEDEWERKER',
  'PROJECT',
  'KLANT',
  'ARTIKELGROEP',
] as const

export const inkoopGrensbedragSchema = z.object({
  scope: z.enum(INKOOP_GRENSBEDRAG_SCOPE),
  rol: z.string().trim().max(50).optional().nullable(),
  medewerkerId: idParam().optional().nullable(),
  bvId: idParam().optional().nullable(),
  artikelgroep: z.string().trim().max(100).optional().nullable(),
  magAanvragenTot: z.number().nonnegative().optional().nullable(),
  magGoedkeurenTot: z.number().nonnegative().optional().nullable(),
  extraGoedkeuringVanaf: z.number().nonnegative().optional().nullable(),
  goedkeuringBijNieuwArtikel: z.boolean().optional().default(false),
  goedkeuringBijNieuweLeverancier: z.boolean().optional().default(true),
})
export type InkoopGrensbedragInput = z.infer<typeof inkoopGrensbedragSchema>

export const inkoopBudgetSchema = z.object({
  scope: z.enum(INKOOP_BUDGET_SCOPE),
  bvId: idParam().optional().nullable(),
  medewerkerId: idParam().optional().nullable(),
  projectId: idParam().optional().nullable(),
  clientId: idParam().optional().nullable(),
  artikelgroep: z.string().trim().max(100).optional().nullable(),
  periodeVan: dateString,
  periodeTot: dateString,
  bedrag: z.number().nonnegative(),
  // FO-392 (batch-46) — budgethouder (Employee) die de uitputting-signalen
  // ontvangt. UI-verplicht bij een nieuw budget; server-side optioneel (leeg ->
  // het signaal valt terug op de administratie-rol, spec A3), zodat bestaande
  // budgetten/clients zonder budgethouder niet breken. De tellers
  // (besteedObligo/besteedGerealiseerd/besteed) staan bewust NIET in dit schema:
  // ze zijn niet handmatig muteerbaar (server-side afgedwongen).
  budgethouderId: idParam().optional().nullable(),
})
export type InkoopBudgetInput = z.infer<typeof inkoopBudgetSchema>
